import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { fetchUnreadCount } from "../api/notifications";
import { useAuth } from "../context/AuthContext";
import { canViewOwnNotifications } from "../utils/access";

/**
 * THE NOTIFICATION ENTRY POINT IN THE HEADER (Part 1 §13).
 *
 * ── IT INVENTS NOTHING ──────────────────────────────────────────────────────
 * One existing endpoint, `GET /notifications/unread-count` (Module 12 EP-02, frozen), reached through
 * the existing `api/notifications.js` client. One existing destination, `/notifications`, which is a
 * registered route already guarded by `canViewOwnNotifications`. No new field is read, no new schema
 * is implied, and NO DEEP LINK IS CONSTRUCTED: §13 forbids inventing `relatedEntity`, `taskId` or
 * `propertyId`, so the bell leads to the inbox and the inbox decides where each message goes.
 *
 * ── THE BADGE IS REAL OR IT IS ABSENT ───────────────────────────────────────
 * The count is DERIVED server-side on every call — Module 12 stores no counter — so there is nothing
 * here to cache into staleness or to approximate. While the request is in flight, while it has
 * failed, and whenever the answer is zero, NO BADGE RENDERS AT ALL. §13's rule is that an
 * unavailable count must not be guessed; a bell with no badge says "nothing to report or nothing
 * known", which is honest, whereas a "0" or a spinner-shaped placeholder would both be claims.
 *
 * `retry: false` matters for the same reason: a failing count should stay silent rather than retry
 * its way into a number.
 *
 * ── IT IS GATED BY THE PREDICATE THAT GUARDS ITS DESTINATION ────────────────
 * `canViewOwnNotifications` — the same one `/notifications` carries in `AppRoutes.jsx`. A door is
 * never offered to somebody the route would refuse. As everywhere else in this panel, that is UX and
 * not security: the endpoint re-checks the caller regardless.
 */
export default function NotificationBell() {
  const { user } = useAuth();
  const allowed = canViewOwnNotifications(user);

  const { data } = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: fetchUnreadCount,
    enabled: allowed,
    // The header is mounted for the whole session, so this is the one query in the panel that has to
    // decide how fresh is fresh enough. A minute is short enough that a new task's notification shows
    // up while the reader is still working, and long enough that the count is not a polling loop.
    staleTime: 60 * 1000,
    retry: false,
  });

  if (!allowed) return null;

  // THE FIELD IS `unread`. `notificationService.getMyUnreadCount` returns `{ unread }` and the
  // controller wraps it in the standard envelope, which `fetchUnreadCount` already unwraps to
  // `r.data.data` — so the payload reaching here is `{ unread: n }`. The customer portal's own bell
  // reads the same field. NO NEW FIELD IS INTRODUCED and none is renamed.
  //
  // Anything else — a failed request, an unexpected shape — leaves `count` null and the badge
  // unrendered, rather than showing a 0 that would be a claim about data nobody has.
  const raw = data?.unread;
  const count = Number.isFinite(raw) && raw > 0 ? raw : null;

  return (
    <Link
      to="/notifications"
      className="btn btn-sm btn-light position-relative header-bell"
      // The icon is the whole control, so `title` alone would leave it announced as "link". The
      // accessible name carries the count when there is one, because a screen-reader user cannot see
      // the badge that sighted readers get for free.
      aria-label={count ? `Notifications, ${count} unread` : "Notifications"}
      title="Notifications"
    >
      <i className="bi bi-bell" aria-hidden="true" />
      {count && (
        // `aria-hidden`: the number is already in the link's accessible name above, and announcing it
        // twice is how a bell reads as "Notifications 3 unread 3".
        <span className="header-bell-badge" aria-hidden="true">
          {count > 99 ? "99+" : count}
        </span>
      )}
    </Link>
  );
}
